/**
 * Signing in to a local agent behind the app's own sign-in screen — desktop only. See agents.rs
 * (agent_login_start and the commands after it).
 *
 * The agent's own sign-in runs with no window; the page reads what it prints, line by line, to
 * show the address to open and to know when a code is asked for and when it is done. The parsing
 * is here, in plain functions, because the wording belongs to each agent's CLI and changes with
 * it — a test can pin it, and the Rust side stays a broker.
 */

import { isDesktop } from './desktop'
import type { LocalAgentId } from './localAgent'

/** The agents whose sign-in runs behind the screen; the others keep the console window. */
export const IN_APP_LOGIN: readonly LocalAgentId[] = ['claude', 'claude-2', 'codex']

export function hasInAppLogin(agent: LocalAgentId): boolean {
  return IN_APP_LOGIN.includes(agent)
}

/** What the sign-in has said so far, as the screen needs it. */
export interface LoginState {
  /** The address to open in the browser, once printed. */
  url: string | null
  /** Whether the sign-in asked for a code to be pasted. */
  asksForCode: boolean
  succeeded: boolean
  /** The last line that reads like a failure, as printed. */
  error: string | null
}

export const INITIAL_LOGIN: LoginState = { url: null, asksForCode: false, succeeded: false, error: null }

// Colours and cursor moves, in case a CLI decides to style its output even on a pipe.
// eslint-disable-next-line no-control-regex
const ANSI = /\u001b\[[0-9;?]*[ -/]*[@-~]|\u001b\][^\u0007]*\u0007/g

/** The first https address in a line, without the punctuation a sentence may put after it. */
export function findUrl(line: string): string | null {
  const match = /https:\/\/[^\s<>"'`]+/.exec(line.replace(ANSI, ''))
  return match ? match[0].replace(/[).,;:!?\]]+$/, '') : null
}

/**
 * Folds one printed line into the state. Claude Code prints "If the browser didn't open, visit:
 * <url>", then "Paste code here if prompted >", then "Login successful."; Codex prints an address
 * and waits for the browser's callback. The first address wins: it is the sign-in's own, and a
 * later one (a help link in an error) must not replace it.
 */
export function readLoginLine(state: LoginState, raw: string): LoginState {
  const line = raw.replace(ANSI, '').trim()
  if (!line) return state

  if (/login successful|successfully logged in|logged in successfully|signed in successfully/i.test(line)) {
    return { ...state, succeeded: true, error: null }
  }

  const url = state.url ?? findUrl(line)
  const asksForCode = state.asksForCode || /paste (the |your )?(authorization )?code|enter (the |your )?(authorization )?code/i.test(line)
  const failed = !findUrl(line) && /\b(error|failed|failure|invalid|expired|denied|timed out|unauthorized)\b/i.test(line)

  return { ...state, url, asksForCode, error: failed ? line : state.error }
}

/** Every line at once — for tests, and for lines that arrived before the screen subscribed. */
export function readLoginLines(lines: string[], state: LoginState = INITIAL_LOGIN): LoginState {
  return lines.reduce(readLoginLine, state)
}

/** How the screen should read the end of the process: success, or the failure to show. */
export function loginOutcome(state: LoginState, code: number | null): 'ok' | 'failed' | 'cancelled' {
  if (state.succeeded || code === 0) return 'ok'
  return code === null ? 'cancelled' : 'failed'
}

/** A pasted code the broker will accept: one line of visible characters, not too long. */
export function cleanCode(code: string): string | null {
  const trimmed = code.trim()
  return trimmed.length > 0 && trimmed.length <= 512 && /^[\x21-\x7e]+$/.test(trimmed) ? trimmed : null
}

async function invoke<T>(command: string, args?: Record<string, unknown>): Promise<T> {
  const { invoke: call } = await import('@tauri-apps/api/core')
  return call<T>(command, args)
}

interface LoginEvent {
  id: string
  line: string | null
  done: boolean
  code: number | null
}

export interface LoginHandlers {
  onLine: (line: string) => void
  onDone: (code: number | null) => void
}

export interface RunningLogin {
  sendCode: (code: string) => Promise<void>
  cancel: () => Promise<void>
}

/**
 * Starts the agent's sign-in and passes what it prints to `handlers`. The broker may print before
 * it has answered with the sign-in's id, so events are held until the id is known and then replayed
 * — the first line is usually the address.
 */
export async function startAgentLogin(agent: LocalAgentId, handlers: LoginHandlers): Promise<RunningLogin> {
  const { listen } = await import('@tauri-apps/api/event')
  let id: string | null = null
  let ended = false
  const early: LoginEvent[] = []

  const deliver = (event: LoginEvent): void => {
    if (ended || event.id !== id) return
    if (event.done) {
      ended = true
      stop()
      handlers.onDone(event.code)
    } else if (event.line !== null) {
      handlers.onLine(event.line)
    }
  }

  const stop = await listen<LoginEvent>('devpulse://agent-login', (event) => {
    if (id === null) early.push(event.payload)
    else deliver(event.payload)
  })

  try {
    id = await invoke<string>('agent_login_start', { agent })
  } catch (error) {
    stop()
    throw error
  }
  for (const event of early.splice(0)) deliver(event)

  return {
    sendCode: async (code) => {
      const clean = cleanCode(code)
      if (!clean) throw new Error('invalid-code')
      await invoke('agent_login_code', { id, code: clean })
    },
    cancel: async () => {
      if (!ended) {
        ended = true
        stop()
      }
      await invoke('agent_login_cancel', { id })
    },
  }
}

/** Whether the agent is signed in: true or false when it can say, null when it cannot. */
export async function agentAuthStatus(agent: LocalAgentId): Promise<boolean | null> {
  if (!isDesktop()) return null
  return invoke<boolean | null>('agent_auth_status', { agent })
}
